// The trace id is minted per page load, so the SSR pageview is on its own trace; a baked window.__TRACE_ID wins.

const uuid = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    // fall through
  }
  return `${Date.now().toString(16)}-${Math.floor(Math.random() * 1e16).toString(16)}`
}

const SESSION_KEY = 'danchiano:session-id'

let traceId: string | undefined
// Must stay DISTINCT from traceId, or session.id === trace.id whenever storage is blocked.
let fallbackSessionId: string | undefined

/** A per-tab session id, persisted in sessionStorage so it survives client navigations and reloads. */
export function getSessionId(): string {
  if (typeof window === 'undefined') return ''
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const fresh = uuid()
    window.sessionStorage.setItem(SESSION_KEY, fresh)
    return fresh
  } catch {
    // sessionStorage can throw (privacy mode / disabled). Fall back to a per-load id.
    fallbackSessionId ??= uuid()
    return fallbackSessionId
  }
}

/** The trace id for this page load. Prefers an SSR-baked `window.__TRACE_ID`; else client-minted once. */
export function getTraceId(): string {
  if (typeof window === 'undefined') return ''
  const baked = (window as unknown as Record<string, unknown>)['__TRACE_ID']
  if (typeof baked === 'string' && baked) return baked
  traceId ??= uuid()
  return traceId
}

/** The SSR render's root span id, if baked into the document; else empty (no parent). */
export function getRootSpanId(): string {
  if (typeof window === 'undefined') return ''
  const baked = (window as unknown as Record<string, unknown>)['__ROOT_SPAN_ID']
  return typeof baked === 'string' ? baked : ''
}

/** A fresh span id per client operation. */
export function newSpanId(): string {
  return uuid()
}

// Shared by main.tsx's tRPC headers() and the session-actions flush so the two cannot drift.
export function correlationHeaders(options?: { includeParentSpan?: boolean }): Record<string, string> {
  const headers: Record<string, string> = {}
  const trace = getTraceId()
  if (trace) headers['x-trace-id'] = trace
  const session = getSessionId()
  if (session) headers['x-session-id'] = session
  headers['x-span-id'] = newSpanId()
  if (options?.includeParentSpan) {
    const rootSpanId = getRootSpanId()
    if (rootSpanId) headers['x-parent-span-id'] = rootSpanId
  }
  return headers
}
